/**
 * Platform Tracking Helper — Meta Pixel + GA4 + Google Ads
 *
 * SDKs are loaded in index.html but NOT initialized there.
 * initTracking() configures the pixel / GA4 property / Google Ads accounts.
 *
 * All IDs, conversion labels, and event name mappings live in
 *   src/config/tracking.ts
 * — edit that file to add a new pixel, GA4 property, Ads account, or event.
 *
 * Usage:
 *   import { initTracking, trackEvent } from '@/utils/metaPixel'
 *   initTracking()
 *   trackEvent('Purchase', { value: 49, currency: 'USD', content_name: 'pro' })
 */

import { useAuthStore } from '@/stores/auth'
import { TRACKING_CONFIG, GOOGLE_ADS_BACKUP, GA4_EVENT_MAP } from '@/config/tracking'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.staging.profitjam.io'

/** Guard against double-initialization */
let initialized = false

/**
 * Logged-in user email, when known. Used to auto-tag events so server-side
 * funnels in admin can compute count(distinct email). Returns undefined for
 * pre-auth events (PageView on register page, etc.) which is fine — those
 * stages don't gate on a logged-in identity.
 */
function resolveAuthEmail(): string | undefined {
  try {
    const authStore = useAuthStore()
    return authStore.user?.email || undefined
  } catch {
    return undefined
  }
}

/** Initialize tracking if it hasn't been already. Returns true when ready. */
export function ensureTracking(): boolean {
  if (initialized) return true
  initTracking()
  return true
}

/**
 * Initialize Meta Pixel, GA4, and Google Ads. Safe to call multiple
 * times — no-op after the first call.
 */
export function initTracking(): void {
  if (initialized) return
  initialized = true

  const config = TRACKING_CONFIG

  const fbq = (window as any).fbq
  const gtag = (window as any).gtag

  // Meta Pixel
  if (typeof fbq === 'function' && config.pixelId) {
    fbq('init', config.pixelId)
    fbq('track', 'PageView')
  }

  // GA4 + Google Ads (primary + backup)
  if (typeof gtag === 'function' && config.ga4Id) {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${config.ga4Id}`
    document.head.appendChild(script)

    gtag('js', new Date())
    gtag('config', config.ga4Id)

    if (config.googleAdsId) gtag('config', config.googleAdsId)
    gtag('config', GOOGLE_ADS_BACKUP.id)
  }
}

/** Generate a UUID v4 for event deduplication */
function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/** Read a cookie value by name */
function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match?.[2] ? decodeURIComponent(match[2]) : undefined
}

/**
 * Track a standard or custom event — fires Meta Pixel (browser), GA4, Google
 * Ads conversions (if labels exist), and the server-side Meta CAPI relay.
 *
 * @param eventName - Standard event (PageView, Purchase, …) or custom (CreatePage)
 * @param customData - Event parameters (value, currency, content_name, …)
 * @param opts.email - Optional unhashed email for server-side user matching
 * @param opts.isCustom - Set true for trackCustom events (default: standard track)
 */
export function trackEvent(
  eventName: string,
  customData: Record<string, unknown> = {},
  opts: { email?: string; isCustom?: boolean } = {}
): void {
  ensureTracking()

  const eventId = uuid()
  const fbq = (window as any).fbq
  const gtag = (window as any).gtag

  // 1. Meta Pixel (browser)
  if (typeof fbq === 'function') {
    const method = opts.isCustom ? 'trackCustom' : 'track'
    fbq(method, eventName, customData, { eventID: eventId })
  }

  // 2. GA4 + Google Ads conversions (browser)
  if (typeof gtag === 'function') {
    // Enhanced conversions for Google Ads: register user-provided data so
    // Google can match this conversion to an ad click. The Google tag handles
    // SHA-256 hashing automatically — pass plain values. user_data persists
    // for the page session, so subsequent events (e.g. Purchase) inherit it.
    if (opts.email) {
      gtag('set', 'user_data', { email: opts.email })
    }

    const ga4Event = GA4_EVENT_MAP[eventName] || eventName.toLowerCase()
    gtag('event', ga4Event, { ...customData, event_id: eventId })

    // Google Ads conversion — fires once per account that has a label mapped
    // for this event. Primary = main account; backup = global (AW-181…55).
    const primaryLabel = TRACKING_CONFIG.googleAdsLabels?.[eventName]
    const backupLabel = GOOGLE_ADS_BACKUP.labels[eventName]

    const sendTo: string[] = []
    if (TRACKING_CONFIG.googleAdsId && primaryLabel) sendTo.push(`${TRACKING_CONFIG.googleAdsId}/${primaryLabel}`)
    if (backupLabel) sendTo.push(`${GOOGLE_ADS_BACKUP.id}/${backupLabel}`)

    if (sendTo.length > 0) {
      gtag('event', 'conversion', {
        send_to: sendTo.length === 1 ? sendTo[0] : sendTo,
        value: customData.value || 0,
        currency: customData.currency || 'USD',
        transaction_id: customData.transaction_id || eventId,
      })
    }
  }

  // 3. Server relay for Meta Conversions API (fire-and-forget)
  const body: Record<string, unknown> = {
    event_name: eventName,
    event_id: eventId,
    source_url: window.location.href,
    custom_data: customData,
  }

  const fbc = getCookie('_fbc')
  const fbp = getCookie('_fbp')
  if (fbc) body.fbc = fbc
  if (fbp) body.fbp = fbp
  // Email tagging: explicit opts.email wins (used for enhanced conversions on
  // CompleteRegistration / Activation), otherwise auto-attach the logged-in
  // user's email so admin funnels can count distinct users without every
  // call site having to thread email through manually.
  const email = opts.email || resolveAuthEmail()
  if (email) body.email = email

  fetch(`${API_URL}/public/meta/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {
    // Silent fail — pixel still fired browser-side
  })
}
